import axios, { AxiosRequestConfig } from "axios";
import { MutableRefObject, ReactNode, useEffect } from "react";
import { useServiceFailureContext } from "@zenith/context/global-context/hooks";

type Props = {
  children: ReactNode;
  lastRequestRef: MutableRefObject<AxiosRequestConfig | null>;
};

/**
 * @component
 * ServiceFailure
 * @description
 * Intercepts all requests made, if any of them returns with
 * the internalCode ZENZ-CROSS-0002 or ZENZ-CROSS-012 it calls
 * the serviceFailureContext and sets the hasError to true
 * @param props
 */
const ServiceFailure = (props: Props) => {
  const { setServiceFailureContext } = useServiceFailureContext();

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      function (response) {
        return response;
      },
      function (error) {
        const internalCode = error.response?.data?.internalCode;
        const responseData = error.response?.data?.data;
        const requestUrl = error.response?.config?.url;

        // Check if this is a payment link request
        const isPaymentLinkRequest = requestUrl?.includes(
          "/api/operation/payment-link/"
        );

        // Check if this is a payment link with selected payment method
        const hasPaymentMethod = responseData?.paymentLink?.paymentLinkMethod;
        const hasBankTransferDetails = responseData?.inBankTransfer;
        const hasSelectedPaymentMethod =
          hasPaymentMethod || hasBankTransferDetails;

        // If this is a payment link with a selected payment method, completely bypass the error
        if (
          isPaymentLinkRequest &&
          error.response?.config?.method === "get" &&
          error.response?.data &&
          (internalCode === "ZENZ-CROSS-002" ||
            internalCode === "ZENZ-CROSS-012") &&
          hasSelectedPaymentMethod
        ) {
          // This is a valid case where payment method is selected but link appears "expired"
          // Completely bypass the error and return a successful response
          console.log(
            "Payment link with selected method, bypassing service failure"
          );
          return Promise.resolve({
            data: {
              success: true,
              message: "Request successfully processed",
              internalCode: "ZENZ-CROSS-001",
              dateTime: new Date().toISOString(),
              data: responseData,
            },
            status: 200,
            statusText: "OK",
            headers: error.response.headers,
            config: error.response.config,
          });
        }

        // Only show service failure for actual errors
        if (
          error.response?.config?.method === "get" &&
          error.response?.data &&
          (internalCode === "ZENZ-CROSS-002" ||
            internalCode === "ZENZ-CROSS-012")
        ) {
          setServiceFailureContext(true);
          props.lastRequestRef.current = error.config;
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [setServiceFailureContext]);

  return props.children;
};

export { ServiceFailure };
